/**
 * ChipButton - Individual suggestion chip using design system Button with Tooltip.
 *
 * Visual differentiation:
 * - Suggested chips: ghost variant, category icon in text-secondary
 * - Protocols (pinned): ghost variant, SlashSquare icon in Pro Purple
 * - Safety chips (red_flag): ghost variant, icon in validation/warning
 * - Loading chips: shimmer text, Loader2 icon
 *
 * Tooltip behavior:
 * - Protocols (pinned): Light-themed tooltip with icon, title, and truncated description
 * - Other chips: Default dark tooltip with query text only
 */

import {
  categoryConfig,
  getCategoryIcon,
  type AutoQueryStatus,
  type SuggestedChip,
} from '@/components/ui/transcript-chips/types';
import { getSuggestedChipPeekDisplayLabel } from '@/helpers/clinical-tasks/documentCreationTask';
import { cn } from '@/lib/cn';
import { Button } from '@heidi/design-system/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@heidi/design-system/tooltip';
import { TypographyCaption, TypographyP2Bold } from '@heidi/design-system/typography';
import { TextShimmer } from '@heidi/snowflake-ui/shimmer-text';
import { Loader2, SlashSquare } from 'lucide-react';
import * as m from 'motion/react-m';
import { memo } from 'react';
import {
  CHIP_INTERACTIVE_CLASS,
  CHIP_SHELL_CLASS,
  chipVariants,
} from './chip-button.utils';

export interface ChipButtonProps {
  /** The chip data (can be a pinned chip with isDefault / teamShortcutId). */
  chip: SuggestedChip & { isDefault?: boolean; teamShortcutId?: string };
  /** Index for stagger animation */
  index: number;
  /** Query status for loading/ready states */
  status?: AutoQueryStatus;
  /** Whether this is a newly appeared chip (triggers glow) */
  isNew?: boolean;
  /** Click handler */
  onChipClick?: (chip: SuggestedChip) => void;
  /** Override button size (default: 'xs') */
  size?: 'xxs' | 'xs' | 'sm' | 'md' | 'lg' | 'touch';
  /** Override button variant (default: 'ghost'). Never escalated to destructive — text stays primary. */
  variant?: 'ghost' | 'outline';
  /** Force Pro Purple icon color (default: false). Pinned chips get Pro Purple automatically. */
  iconProPurple?: boolean;
  /** Optional overlay rendered inside the chip's root element (e.g., a remove button) */
  overlay?: React.ReactNode;
  /** Additional classes applied to the root wrapper element */
  wrapperClassName?: string;
  /**
   * Count of visible suggested chips in the strip (peek collapsed row or floating bar).
   * When `1`, document-draft chips use `short_title` via `getSuggestedChipPeekDisplayLabel`.
   */
  suggestedChipsCount?: number;
}

export const ChipButton = memo(
  ({
    chip,
    index,
    status,
    isNew,
    onChipClick,
    size = 'xs',
    variant = 'ghost',
    iconProPurple = false,
    overlay,
    wrapperClassName,
    suggestedChipsCount,
  }: ChipButtonProps) => {
    const labelText = getSuggestedChipPeekDisplayLabel(chip, {
      soleVisibleSuggestedChip:
        suggestedChipsCount !== undefined && suggestedChipsCount === 1,
    });
    const tooltipTitle = chip.title ?? chip.label;
    const isPinned = chip?.isPermanent;
    const isLoading = status === 'generating' || status === 'updating';

    // Check if this category is safety-critical (red_flag)
    const config = categoryConfig[chip?.category];
    const isDestructive = config?.isDestructive ?? false;

    // Button variant is never escalated to destructive — text label stays text/primary
    const buttonVariant = variant;

    // Icon color hierarchy: destructive → warning, pinned/explicit → Pro Purple, default → secondary
    const iconColorClass = isDestructive
      ? 'text-validation-warning'
      : isPinned || iconProPurple
        ? 'text-tier-pro-text'
        : 'text-text-secondary';

    return (
      <Tooltip delayDuration={400}>
        <TooltipTrigger asChild>
          <m.div
            custom={index}
            variants={chipVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            layout
            className={cn(
              'group/chip relative z-10 shrink-0 overflow-visible',
              isNew && 'animate-chip-glow',
              wrapperClassName
            )}
          >
            <Button
              data-chip-id={chip.id}
              variant={buttonVariant}
              size={size}
              onClick={() => onChipClick?.(chip)}
              disabled={isLoading}
              className={cn(
                CHIP_SHELL_CLASS,
                isLoading && 'cursor-wait',
                // Hover lift and press response — a loading chip is not pressable.
                !isLoading && CHIP_INTERACTIVE_CLASS,
                // Destructive chips: subtle warning fill on hover
                isDestructive &&
                  'hover:bg-validation-warning-muted active:bg-validation-warning/15'
              )}
            >
              {isLoading && <Loader2 className={cn('animate-spin', iconColorClass)} />}
              {isPinned && !isLoading && <SlashSquare className={iconColorClass} />}
              {!isPinned &&
                !isLoading &&
                (() => {
                  const CategoryIcon = getCategoryIcon(chip.category);
                  return <CategoryIcon className={iconColorClass} />;
                })()}
              {isLoading ? (
                <TextShimmer duration={1.5} spread={3}>
                  {labelText}
                </TextShimmer>
              ) : (
                <span className="whitespace-nowrap">{labelText}</span>
              )}
            </Button>
            {overlay}
          </m.div>
        </TooltipTrigger>
        <TooltipContent
          side="top"
          sideOffset={8}
          className="max-w-sm rounded-lg border border-border bg-surface-elevated p-0 text-text-primary"
        >
          {(() => {
            const TooltipIcon = isPinned ? SlashSquare : getCategoryIcon(chip.category);
            return (
              <div className="grid grid-cols-[auto_minmax(0,1fr)] items-start gap-3 p-3">
                <div
                  className={cn(
                    'flex size-9 shrink-0 items-center justify-center rounded-lg',
                    isDestructive ? 'bg-validation-warning-muted' : 'bg-tier-pro-muted'
                  )}
                >
                  <TooltipIcon
                    size={18}
                    className={
                      isDestructive ? 'text-validation-warning' : 'text-tier-pro-text'
                    }
                  />
                </div>
                <div className="flex min-w-0 flex-col gap-1">
                  <TypographyP2Bold className="line-clamp-1 text-text-primary">
                    {tooltipTitle}
                  </TypographyP2Bold>
                  <TypographyCaption className="line-clamp-3 text-text-secondary">
                    {chip.query}
                  </TypographyCaption>
                </div>
              </div>
            );
          })()}
        </TooltipContent>
      </Tooltip>
    );
  }
);

ChipButton.displayName = 'ChipButton';

export default ChipButton;
